import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Alert, AlertDescription } from '../../ui/alert';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { userService } from '../../../_services';

function AddUserDialog({ open, onOpenChange, onSuccess }) {
    const [formData, setFormData] = useState({
        Fname: '',
        Lname: '',
        Phone: '',
        Email: '',
        isuserpass: false,
        Username: '',
        Password: ''
    });
    
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

    const handleInputChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
        setSubmitMessage({ type: '', text: '' });
    };

    const handlePhoneChange = (value) => {
        const phoneRegex = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;
        if (value === '' || phoneRegex.test(value)) {
            handleInputChange('Phone', value);
        }
    };

    const handleNameChange = (name, value) => {
        const nameRegex = /^[a-zA-Z]*$/;
        if (value === '' || nameRegex.test(value)) {
            handleInputChange(name, value);
        }
    };

    const validate = async () => {
        const newErrors = {};
        
        if (formData.Fname.length < 3) {
            newErrors.Fname = 'First Name must be at least 3 characters long';
        }
        
        if (formData.Lname.length < 2) {
            newErrors.Lname = 'Last Name must be at least 2 characters long';
        }
        
        if (!formData.Phone) {
            newErrors.Phone = 'Phone Number is required';
        } else {
            const cleanPhone = formData.Phone.replace(/[- )(]/g, '');
            if (cleanPhone.length !== 10) {
                newErrors.Phone = 'Phone Number must be 10 digits';
            } else {
                // Validate phone via API
                try {
                    const phoneValidation = await userService.GetPhoneNoValidate(formData.Phone);
                    if (phoneValidation && phoneValidation[0]?.code !== "1") {
                        newErrors.Phone = 'Invalid Phone Number';
                    }
                } catch (error) {
                    newErrors.Phone = 'Error validating phone number';
                }
            }
        }
        
        if (!formData.Email) {
            newErrors.Email = 'Email is required';
        } else if (!userService.ValidEmailRegex(formData.Email)) {
            newErrors.Email = 'Invalid Email';
        }
        
        if (formData.isuserpass) {
            if (formData.Username.length < 5) {
                newErrors.Username = 'Username must be at least 5 characters long';
            }
            if (formData.Username.includes(' ')) {
                newErrors.Username = 'Username does not allow space';
            }
            if (formData.Password.length < 6) {
                newErrors.Password = 'Password must be at least 6 characters long';
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        setLoading(true);
        setSubmitMessage({ type: '', text: '' });

        try {
            const newErrors = await validate();
            
            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                setLoading(false);
                return;
            }

            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [UserId, UserName] = Usermeta_Data.split(':');

            const apidata = await userService.AddNewUser(
                UserId,
                UserName,
                formData.Fname,
                formData.Lname,
                formData.Phone,
                formData.Email,
                formData.Username || '',
                formData.Password || ''
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].code === "1") {
                    setSubmitMessage({ type: 'success', text: 'User added successfully!' });
                    if (onSuccess) onSuccess();
                    setTimeout(() => {
                        handleClose();
                    }, 1500);
                } else {
                    setSubmitMessage({ type: 'error', text: apidata[0].message || 'Error adding user' });
                }
            } else {
                setSubmitMessage({ type: 'error', text: 'Error adding user' });
            }
        } catch (error) {
            console.error('Error adding user:', error);
            setSubmitMessage({ type: 'error', text: 'Error adding user' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            Fname: '',
            Lname: '',
            Phone: '',
            Email: '',
            isuserpass: false,
            Username: '',
            Password: ''
        });
        setErrors({});
        setSubmitMessage({ type: '', text: '' });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-brand-dark">
                        Add New User
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="Fname">First Name *</Label>
                            <Input
                                id="Fname"
                                value={formData.Fname}
                                onChange={(e) => handleNameChange('Fname', e.target.value)}
                                className={errors.Fname ? 'border-red-500' : ''}
                                placeholder="Enter first name"
                            />
                            {errors.Fname && <p className="text-sm text-red-500">{errors.Fname}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="Lname">Last Name *</Label>
                            <Input
                                id="Lname"
                                value={formData.Lname}
                                onChange={(e) => handleNameChange('Lname', e.target.value)}
                                className={errors.Lname ? 'border-red-500' : ''}
                                placeholder="Enter last name"
                            />
                            {errors.Lname && <p className="text-sm text-red-500">{errors.Lname}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="Phone">Phone Number *</Label>
                            <Input
                                id="Phone"
                                value={formData.Phone}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                className={errors.Phone ? 'border-red-500' : ''}
                                placeholder="(555) 555-5555"
                            />
                            {errors.Phone && <p className="text-sm text-red-500">{errors.Phone}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="Email">Email *</Label>
                            <Input
                                id="Email"
                                type="email"
                                value={formData.Email}
                                onChange={(e) => handleInputChange('Email', e.target.value)}
                                className={errors.Email ? 'border-red-500' : ''}
                                placeholder="user@example.com"
                            />
                            {errors.Email && <p className="text-sm text-red-500">{errors.Email}</p>}
                        </div>

                        <div className="space-y-2 col-span-2">
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="isuserpass"
                                    checked={formData.isuserpass}
                                    onCheckedChange={(checked) => handleInputChange('isuserpass', checked)}
                                />
                                <Label htmlFor="isuserpass" className="cursor-pointer">
                                    Create Username and Password
                                </Label>
                            </div>
                        </div>

                        {formData.isuserpass && (
                            <>
                                <div className="space-y-2">
                                    <Label htmlFor="Username">Username *</Label>
                                    <Input
                                        id="Username"
                                        value={formData.Username}
                                        onChange={(e) => handleInputChange('Username', e.target.value)}
                                        className={errors.Username ? 'border-red-500' : ''}
                                        placeholder="Enter username (min 5 characters)"
                                    />
                                    {errors.Username && <p className="text-sm text-red-500">{errors.Username}</p>}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="Password">Password *</Label>
                                    <Input
                                        id="Password"
                                        type="password"
                                        value={formData.Password}
                                        onChange={(e) => handleInputChange('Password', e.target.value)}
                                        className={errors.Password ? 'border-red-500' : ''}
                                        placeholder="Enter password (min 6 characters)"
                                    />
                                    {errors.Password && <p className="text-sm text-red-500">{errors.Password}</p>}
                                </div>
                            </>
                        )}
                    </div>

                    {submitMessage.text && (
                        <Alert 
                            variant={submitMessage.type === 'success' ? 'default' : 'destructive'} 
                            className="mt-4"
                        >
                            {submitMessage.type === 'success' ? (
                                <CheckCircle className="h-4 w-4" />
                            ) : (
                                <AlertCircle className="h-4 w-4" />
                            )}
                            <AlertDescription>{submitMessage.text}</AlertDescription>
                        </Alert>
                    )}

                    <DialogFooter className="mt-6">
                        <Button
                            type="button"
                            variant="default"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="secondary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Adding User...
                                </>
                            ) : (
                                'Add User'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default AddUserDialog;
